// ============================================================================
// InsightChartCard — mounts a sandboxed widget inline in the chat
// Header 'INSIGHT', 220px max body, footer with a 'Pin to dashboard' capsule.
// Shimmer skeleton while code loads (no spinner), quiet error + Try again.
// ============================================================================

import { useMemo, useState } from 'react'
import { Box, Typography, ButtonBase } from '@mui/material'
import { beoflowColors, typography, spacing, borderRadius } from '@/theme/muiTheme'
import { WidgetSandbox } from '@/lib/widget-sandbox'
import { useWidget } from '@/services/api/widgets'
import { useViews, useAddWidgetToView } from '@/services/api/views'
import type { RenderPayload, ChartSpec } from '@/types/chatRenders'
import CardShell, { monoMetaSx } from './CardShell'

const BODY_MAX_HEIGHT = 220

function Skeleton() {
  return (
    <Box
      sx={{
        height: 120,
        borderRadius: borderRadius.medium,
        background:
          'linear-gradient(90deg, rgba(248,246,244,0.04) 25%, rgba(248,246,244,0.09) 50%, rgba(248,246,244,0.04) 75%)',
        backgroundSize: '200% 100%',
        animation: 'insight-shimmer 1.4s ease-in-out infinite',
        '@keyframes insight-shimmer': {
          '0%': { backgroundPosition: '200% 0' },
          '100%': { backgroundPosition: '-200% 0' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    />
  )
}

interface InsightChartCardProps {
  render: RenderPayload
  /** Widget code when it rode on the tool result — skips the refetch. */
  code?: string
}

export default function InsightChartCard({ render, code }: InsightChartCardProps) {
  const spec = render.spec as unknown as Partial<ChartSpec>
  const widgetId = spec.widget_id != null ? String(spec.widget_id) : null
  const title = spec.title ?? render.entity.title

  // Only fetch when the result didn't carry the code inline.
  const widgetQuery = useWidget(code ? null : widgetId)
  const resolvedCode = code ?? widgetQuery.data?.code ?? null

  // Bump to remount the sandbox on "Try again" after a render error.
  const [attempt, setAttempt] = useState(0)
  const [sandboxError, setSandboxError] = useState<string | null>(null)

  // Pin to dashboard: v1 pins to the athlete's default (or first) view.
  const viewsQuery = useViews()
  const addWidget = useAddWidgetToView()
  const [pinned, setPinned] = useState(false)
  const targetView = useMemo(() => {
    const views = viewsQuery.data ?? []
    return views.find(v => v.is_default) ?? views[0] ?? null
  }, [viewsQuery.data])

  const numericWidgetId = widgetId != null ? Number(widgetId) : NaN
  const canPin = targetView != null && Number.isFinite(numericWidgetId) && !pinned && !addWidget.isPending

  const handlePin = () => {
    if (!canPin || !targetView) return
    addWidget.mutate(
      { viewId: targetView.id, widget_id: numericWidgetId },
      { onSuccess: () => setPinned(true) }
    )
  }

  const isLoading = !resolvedCode && !code && (widgetQuery.isLoading || widgetQuery.isFetching)
  const loadFailed = !resolvedCode && !isLoading

  return (
    <CardShell kindLabel="Insight">
      <Typography noWrap sx={{ fontSize: '14px', fontWeight: typography.fontWeightSemibold, mb: spacing.xs }}>
        {title}
      </Typography>

      {/* Body: the sandboxed widget, capped at 220px */}
      {sandboxError || loadFailed ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.sm,
            py: spacing.md,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.sm, minWidth: 0 }}>
            <Box
              component="span"
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: `1px solid ${beoflowColors.redBase}`,
                color: beoflowColors.redBase,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: typography.fontFamilyMonospace,
                fontSize: '10px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              !
            </Box>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              Couldn't load this chart.
            </Typography>
          </Box>
          <Typography
            component="span"
            onClick={() => {
              setSandboxError(null)
              setAttempt(a => a + 1)
              if (loadFailed) void widgetQuery.refetch()
            }}
            sx={{
              ...monoMetaSx,
              color: beoflowColors.meanBlue,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Try again
          </Typography>
        </Box>
      ) : isLoading ? (
        <Skeleton />
      ) : resolvedCode ? (
        <Box sx={{ maxHeight: BODY_MAX_HEIGHT, overflow: 'hidden' }}>
          <WidgetSandbox
            key={attempt}
            code={resolvedCode}
            widgetId={widgetId ?? undefined}
            context="chat"
            onError={err => setSandboxError(err)}
          />
        </Box>
      ) : null}

      {/* Footer: pin capsule */}
      {(targetView || pinned) && !sandboxError && !loadFailed && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: spacing.sm,
            pt: spacing.sm,
            borderTop: '1px solid rgba(248, 246, 244, 0.09)',
          }}
        >
          {pinned ? (
            /* Pinned confirmation: plain teal mono text, no button (frames). */
            <Typography
              component="span"
              sx={{
                fontFamily: typography.fontFamilyMonospace,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: beoflowColors.tealBase,
              }}
            >
              ✓ Pinned{targetView ? ` to ${targetView.title ?? 'view'}` : ''}
            </Typography>
          ) : (
            <ButtonBase
              onClick={handlePin}
              disabled={!canPin}
              sx={{
                borderRadius: borderRadius.round,
                border: `1px solid ${beoflowColors.meanBlue}80`,
                backgroundColor: 'rgba(74, 155, 184, 0.14)',
                px: spacing.md,
                py: '5px',
                fontFamily: typography.fontFamilyMonospace,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: 'text.primary',
                transition: 'background-color 150ms ease',
                '&:hover': { backgroundColor: 'rgba(74, 155, 184, 0.24)' },
              }}
            >
              {addWidget.isPending ? 'Pinning…' : 'Pin to dashboard'}
            </ButtonBase>
          )}
        </Box>
      )}
    </CardShell>
  )
}
